import React, { useEffect, useMemo, useState } from "react";
import { freeOrganizeText } from "./freeOrganizer";
import { translateLiveBridgeText } from "./liveBridgeApi";
import { ResultTabs } from "./ResultTabs";
import { clearSession, loadSession, saveSession } from "./sessionStore";
import { useSpeechRecognition } from "./useSpeechRecognition";

const DEFAULT_TEXT = {
  livebridge_organize_intro:
    "Free local mode: capture English or Chinese speech and turn it into clear, usable text.",
  livebridge_mode_organize: "Organize Mode",
  livebridge_status_listening: "Listening",
  livebridge_status_processing: "Processing",
  livebridge_status_done: "Done",
  livebridge_status_error: "Error",
  livebridge_status_idle: "Idle",
  livebridge_speech_ready:
    "Speech capture is available in this browser for English or Chinese.",
  livebridge_speech_unavailable:
    "Browser speech recognition is not supported here. Paste text instead.",
  livebridge_source_language: "Source",
  livebridge_target_language: "Translate to",
  livebridge_language_english: "English",
  livebridge_language_chinese: "Chinese",
  livebridge_record_start: "Start Listening",
  livebridge_record_stop: "Stop",
  livebridge_recording_active: "Listening for speech now.",
  livebridge_recording_stopping: "Stopping listening…",
  livebridge_organize_button: "Organize Text",
  livebridge_live_preview: "Live Transcript",
  livebridge_live_preview_empty: "Your speech transcript will appear here.",
  livebridge_use_transcript_input: "Use Transcript as Input",
  livebridge_paste_text: "Or paste text",
  livebridge_paste_placeholder: "Paste text here...",
  livebridge_organize_empty: "Nothing yet.",
  livebridge_result_placeholder: "Record your voice or paste text to begin.",
  livebridge_organize_processing: "Processing...",
  livebridge_organize_ready: "Organized result ready.",
  livebridge_translation_ready: "Organized result with translation is ready.",
  livebridge_translation_partial:
    "Organized result is ready. Translation could only be approximated this time.",
  livebridge_translation_failed:
    "Organized result is ready, but translation could not be completed right now.",
  livebridge_organize_mock_ready: "Mock organized result ready.",
  livebridge_organize_failed: "Could not process the text.",
  livebridge_organize_missing_text: "Please record some speech or paste text first.",
  livebridge_result_title: "Title",
  livebridge_result_summary: "Summary",
  livebridge_action_copy: "Copy",
  livebridge_action_download: "Download TXT",
  livebridge_action_retry: "Retry",
  livebridge_tab_transcript: "Transcript",
  livebridge_tab_cleaned: "Cleaned",
  livebridge_tab_structured: "Structured",
  livebridge_tab_translation: "Translation",
  livebridge_result_tabs: "Result tabs",
  livebridge_translation_placeholder:
    "Translation could not be generated right now. Try again in a moment.",
  livebridge_key_points: "Key Points",
  livebridge_action_items: "Action Items",
  livebridge_clear: "Clear"
};

const LANGUAGE_MAP = {
  en: "en-US",
  zh: "zh-CN"
};

const SOURCE_LANGUAGE_OPTIONS = [
  { value: "en", labelKey: "livebridge_language_english" },
  { value: "zh", labelKey: "livebridge_language_chinese" }
];

function getOppositeLanguage(sourceLanguage) {
  return sourceLanguage === "zh" ? "en" : "zh";
}

function normalizeLanguage(value, fallback) {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return fallback;
  }

  if (normalizedValue === "en" || normalizedValue === "en-US") {
    return "en";
  }

  if (normalizedValue === "zh" || normalizedValue === "zh-CN" || normalizedValue === "zh-TW") {
    return "zh";
  }

  return fallback;
}

function fallbackTranslate(key) {
  return DEFAULT_TEXT[key] || key;
}

function LiveBridgeOrganizeComponent({ t = fallbackTranslate }) {
  const [sourceLanguage, setSourceLanguage] = useState("en");
  const [targetLanguage, setTargetLanguage] = useState("zh");
  const [manualText, setManualText] = useState("");
  const [rawTranscript, setRawTranscript] = useState("");
  const [result, setResult] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [appStatus, setAppStatus] = useState("idle");
  const [statusNote, setStatusNote] = useState("");
  const [error, setError] = useState("");

  const {
    supported: speechSupported,
    listening,
    finalText,
    interimText,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript
  } = useSpeechRecognition(LANGUAGE_MAP[sourceLanguage] || "en-US");

  const currentTranscript = useMemo(() => {
    return [finalText, interimText].filter(Boolean).join(" ").trim();
  }, [finalText, interimText]);

  const workingText = useMemo(() => {
    return String(manualText || currentTranscript || rawTranscript || "").trim();
  }, [manualText, currentTranscript, rawTranscript]);

  useEffect(() => {
    const savedSession = loadSession();

    if (!savedSession) {
      return;
    }

    const normalizedResult = savedSession.result
      ? {
          ...savedSession.result,
          rawTranscript:
            savedSession.result.rawTranscript || savedSession.result.transcript || "",
          cleanText: savedSession.result.cleanText || savedSession.result.cleanedText || ""
        }
      : null;

    const nextSourceLanguage = normalizeLanguage(savedSession.sourceLanguage, "en");
    const nextTargetLanguage = normalizeLanguage(
      savedSession.targetLanguage,
      getOppositeLanguage(nextSourceLanguage)
    );

    setSourceLanguage(nextSourceLanguage);
    setTargetLanguage(
      nextTargetLanguage === nextSourceLanguage
        ? getOppositeLanguage(nextSourceLanguage)
        : nextTargetLanguage
    );
    setManualText(String(savedSession.manualText || ""));
    setResult(normalizedResult);
    setRawTranscript(String(normalizedResult?.rawTranscript || ""));
  }, []);

  useEffect(() => {
    saveSession({
      sourceLanguage,
      targetLanguage,
      manualText,
      result
    });
  }, [sourceLanguage, targetLanguage, manualText, result]);

  useEffect(() => {
    if (targetLanguage === sourceLanguage) {
      setTargetLanguage(getOppositeLanguage(sourceLanguage));
    }
  }, [sourceLanguage, targetLanguage]);

  const statusLabel =
    appStatus === "listening"
      ? t("livebridge_status_listening")
      : appStatus === "processing"
      ? t("livebridge_status_processing")
      : appStatus === "done"
      ? t("livebridge_status_done")
      : appStatus === "error"
      ? t("livebridge_status_error")
      : t("livebridge_status_idle");

  const displayResult = useMemo(() => {
    if (result) {
      return result;
    }

    return {
      rawTranscript: workingText,
      cleanText: "",
      structuredNotes: "",
      translatedText: "",
      title: "",
      summary: "",
      keyPoints: [],
      actionItems: []
    };
  }, [result, workingText]);

  const handleOrganize = async (baseTranscript) => {
    const normalizedTranscript = String(baseTranscript || "").trim();

    if (!normalizedTranscript) {
      setError(t("livebridge_organize_missing_text"));
      setAppStatus("error");
      return;
    }

    setIsBusy(true);
    setAppStatus("processing");
    setError("");
    setStatusNote(t("livebridge_organize_processing"));

    try {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      const payload = freeOrganizeText(normalizedTranscript, sourceLanguage);
      const normalizedTargetLanguage =
        targetLanguage === sourceLanguage
          ? getOppositeLanguage(sourceLanguage)
          : targetLanguage;
      let translatedText = "";
      let translationMode = "none";

      try {
        const translationPayload = await translateLiveBridgeText({
          text: payload.cleanText || normalizedTranscript,
          sourceLanguage,
          targetLanguage: normalizedTargetLanguage
        });

        translatedText = String(translationPayload?.translatedText || "").trim();
        translationMode = String(translationPayload?.mode || "").trim() || "local";
      } catch (_translationError) {
        translatedText = "";
        translationMode = "error";
      }

      const finalPayload = {
        ...payload,
        targetLanguage: normalizedTargetLanguage,
        translatedText,
        translationMode
      };

      setRawTranscript(payload.rawTranscript || normalizedTranscript);
      setResult(finalPayload);
      setAppStatus("done");
      if (translatedText && translationMode !== "local") {
        setStatusNote(t("livebridge_translation_ready"));
      } else if (translatedText) {
        setStatusNote(t("livebridge_translation_partial"));
      } else {
        setStatusNote(
          payload.mode === "mock"
            ? t("livebridge_organize_mock_ready")
            : t("livebridge_organize_ready")
        );
      }
    } catch (nextError) {
      setAppStatus("error");
      setError(
        nextError instanceof Error && nextError.message
          ? nextError.message
          : t("livebridge_organize_failed")
      );
    } finally {
      setIsBusy(false);
    }
  };

  const handleStartListening = () => {
    setError("");
    setResult(null);
    resetTranscript();
    const started = startListening();

    if (started) {
      setAppStatus("listening");
      setStatusNote(t("livebridge_recording_active"));
    } else {
      setAppStatus("error");
      setError(t("livebridge_speech_unavailable"));
    }
  };

  const handleStopListening = () => {
    stopListening();
    setAppStatus("idle");
    setStatusNote(t("livebridge_recording_stopping"));
  };

  const handleRetry = async () => {
    await handleOrganize(rawTranscript || workingText);
  };

  const handleUseTranscriptAsInput = () => {
    setManualText(String(currentTranscript || "").trim());
  };

  return (
    <div className="liveBridgeOrganize">
      <div className="liveBridgeModeLead">
        <p>{t("livebridge_organize_intro")}</p>
      </div>

      <div className="liveBridgeOrganizeLayout">
        <section className="liveBridgeOrganizePanel">
          <div className="liveBridgeStatusRow">
            <span className="liveBridgeStatus">{t("livebridge_mode_organize")}</span>
            <span className="liveBridgeStatus">{statusLabel}</span>
            <span className="liveBridgeStatus">
              {speechSupported
                ? t("livebridge_speech_ready")
                : t("livebridge_speech_unavailable")}
            </span>
          </div>

          <div className="liveBridgeControlGroup">
            <div className="liveBridgeField liveBridgeFieldInline">
              <label className="liveBridgeFieldLabel" htmlFor="livebridge-organize-source">
                {t("livebridge_source_language")}
              </label>
              <select
                id="livebridge-organize-source"
                className="liveBridgeFieldInput liveBridgeFieldSelect"
                value={sourceLanguage}
                onChange={(event) => setSourceLanguage(event.target.value)}
                disabled={listening || isBusy}
              >
                {SOURCE_LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <div className="liveBridgeField liveBridgeFieldInline">
              <label className="liveBridgeFieldLabel" htmlFor="livebridge-organize-target">
                {t("livebridge_target_language")}
              </label>
              <select
                id="livebridge-organize-target"
                className="liveBridgeFieldInput liveBridgeFieldSelect"
                value={targetLanguage}
                onChange={(event) => setTargetLanguage(event.target.value)}
                disabled={listening || isBusy}
              >
                {SOURCE_LANGUAGE_OPTIONS.filter((option) => option.value !== sourceLanguage).map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          <div className="liveBridgeControlRow">
            <button
              className="primaryButton liveBridgeButtonLive"
              type="button"
              onClick={handleStartListening}
              disabled={listening || isBusy || !speechSupported}
            >
              {t("livebridge_record_start")}
            </button>
            <button
              className="homeSecondaryButton liveBridgeButtonStop"
              type="button"
              onClick={handleStopListening}
              disabled={!listening}
            >
              {t("livebridge_record_stop")}
            </button>
            <button
              className="primaryButton liveBridgeButtonOrganize"
              type="button"
              onClick={() => handleOrganize(workingText)}
              disabled={!workingText || isBusy}
            >
              {t("livebridge_organize_button")}
            </button>
          </div>

          <div className="liveBridgeField">
            <label className="liveBridgeFieldLabel" htmlFor="livebridge-organize-preview">
              {t("livebridge_live_preview")}
            </label>
            <div id="livebridge-organize-preview" className="liveBridgeTranscriptPreview">
              {currentTranscript || t("livebridge_live_preview_empty")}
            </div>
          </div>

          <div className="liveBridgeControlRow">
            <button
              className="homeSecondaryButton liveBridgeButtonUtility"
              type="button"
              onClick={handleUseTranscriptAsInput}
              disabled={!finalText}
            >
              {t("livebridge_use_transcript_input")}
            </button>
            <button
              className="homeSecondaryButton liveBridgeButtonUtility"
              type="button"
              onClick={() => {
                resetTranscript();
                setManualText("");
                setRawTranscript("");
                setResult(null);
                setError("");
                setStatusNote("");
                setAppStatus("idle");
                clearSession();
              }}
              disabled={isBusy}
            >
              {t("livebridge_clear")}
            </button>
          </div>

          <div className="liveBridgeField">
            <label className="liveBridgeFieldLabel" htmlFor="livebridge-organize-paste">
              {t("livebridge_paste_text")}
            </label>
            <textarea
              id="livebridge-organize-paste"
              className="liveBridgeFieldTextarea"
              value={manualText}
              onChange={(event) => setManualText(event.target.value)}
              placeholder={t("livebridge_paste_placeholder")}
              rows={8}
              maxLength={2000}
            />
          </div>

          {statusNote ? <p className="liveBridgeSessionNote">{statusNote}</p> : null}
          {speechError ? <p className="liveBridgeSessionNote">{speechError}</p> : null}
          {error ? <p className="liveBridgeErrorText">{error}</p> : null}
        </section>

        <section className="liveBridgeOrganizePanel liveBridgeOrganizeResults">
          {result ? (
            <ResultTabs result={displayResult} onRetry={handleRetry} t={t} />
          ) : (
            <div className="liveBridgeResultPlaceholder">
              {t("livebridge_result_placeholder")}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export { LiveBridgeOrganizeComponent as LiveBridgeOrganize };

export default LiveBridgeOrganizeComponent;
